import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { 
  Lock, Star, Crown, Mic, Download, Zap, Sparkles, 
  BookOpen, Wand2, ArrowRight, Gift, Check
} from 'lucide-react';

const CAT_EXCITED = "https://static.prod-images.emergentagent.com/jobs/9146f3d9-a3b8-4907-acc0-5321df3c8e86/images/e0aebdb4136ccfbb07836d8a4918b929783a732c1a4be048d63bbb818f0ba07c.png";

// Different upgrade prompts for different features
const UPGRADE_PROMPTS = {
  export: {
    icon: Download,
    title: "Export Your Story",
    description: "Download your book as PDF, DOCX, or TXT to share with editors, publishers, or just keep a copy.",
    features: [
      "Export to PDF (print-ready)",
      "Export to DOCX (Word editing)",
      "Export to TXT (plain text)",
    ],
    minTier: "plus",
    cta: "Upgrade to Export"
  },
  import: {
    icon: Download,
    title: "Import Your Files",
    description: "Bring your existing documents into Shweebum for AI-powered writing assistance.",
    features: [
      "Import PDF, DOCX, TXT files",
      "Smart content extraction",
      "Preserve your formatting",
    ],
    minTier: "plus",
    cta: "Upgrade to Import"
  },
  voice: {
    icon: Mic,
    title: "AI Voice Reader",
    description: "Listen to your chapters read aloud. Perfect for catching awkward phrasing and checking dialogue flow.",
    features: [
      "Natural-sounding AI voices",
      "Adjustable reading speed",
      "Great for editing & proofing",
    ],
    minTier: "plus",
    cta: "Upgrade for Voice"
  },
  fullBook: {
    icon: Zap,
    title: "Full Book Generation",
    description: "One click = your entire book written! AI takes your outline and writes every chapter automatically.",
    features: [
      "Generate all chapters at once",
      "Consistent story throughout",
      "Save hours of writing time",
    ],
    minTier: "premium",
    cta: "Upgrade for Full Book"
  },
  continueStyle: {
    icon: Wand2,
    title: "Continue My Style",
    description: "Train the AI to write exactly like YOU. Upload samples and watch as Shweebum matches your voice.",
    features: [
      "AI learns your word choices",
      "Matches your sentence rhythms",
      "Captures your unique tone",
    ],
    minTier: "premium",
    cta: "Upgrade for Style Match"
  },
  moreProjects: {
    icon: BookOpen,
    title: "More Book Projects",
    description: "You've reached your project limit! Upgrade to work on more books simultaneously.",
    features: [
      "Plus: 10 projects, 30 chapters each",
      "Premium: 100+ projects, unlimited chapters",
      "All your stories in one place",
    ],
    minTier: "plus",
    cta: "Upgrade for More Books"
  },
  moreChapters: {
    icon: BookOpen,
    title: "More Chapters",
    description: "Your book is growing! Upgrade to add more chapters beyond the free limit.",
    features: [
      "Free: 15 chapters per book",
      "Plus: 30 chapters per book",
      "Premium: Unlimited chapters",
    ],
    minTier: "plus",
    cta: "Upgrade for More Chapters"
  },
  saved: {
    icon: Gift,
    title: "Your Book is Saved!",
    description: "Great progress! With Shweebum Plus or Premium, you can unlock even more powerful features:",
    features: [
      "Export your book as PDF/DOCX",
      "AI Voice reads your chapters aloud",
      "Premium AI writes richer prose",
      "Full Book Generation (Premium)",
    ],
    minTier: "plus",
    cta: "See Upgrade Options",
    isUpsell: true
  }
};

export const UpgradePrompt = ({ 
  open, 
  onClose, 
  feature = 'export', 
  currentTier = 'free' 
}) => {
  const prompt = UPGRADE_PROMPTS[feature] || UPGRADE_PROMPTS.export;
  const Icon = prompt.icon;
  const isPremiumOnly = prompt.minTier === 'premium';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700/60 p-0 overflow-hidden">
        {/* Header */}
        <div className={`p-6 text-center ${
          isPremiumOnly 
            ? 'bg-gradient-to-r from-violet-600/60 to-purple-600/60' 
            : prompt.isUpsell
              ? 'bg-gradient-to-r from-emerald-600/60 to-cyan-600/60'
              : 'bg-gradient-to-r from-cyan-600/60 to-blue-600/60'
        }`}>
          <div className="flex items-center justify-center gap-3 mb-3">
            <img 
              src={CAT_EXCITED} 
              alt="Shweebum" 
              className="w-14 h-14 object-contain drop-shadow-lg"
            />
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
              isPremiumOnly 
                ? 'bg-violet-500/30 border border-violet-500/50' 
                : prompt.isUpsell
                  ? 'bg-emerald-500/30 border border-emerald-500/50'
                  : 'bg-cyan-500/30 border border-cyan-500/30'
            }`}>
              {prompt.isUpsell ? (
                <Sparkles className="w-7 h-7 text-emerald-300" />
              ) : (
                <Lock className="w-7 h-7 text-white/80" />
              )}
            </div>
          </div>
          
          <h2 className="text-xl font-bold text-white">{prompt.title}</h2>
          <div className="flex items-center justify-center gap-2 mt-2">
            {isPremiumOnly ? (
              <span className="text-xs bg-violet-500/30 text-violet-200 px-2 py-1 rounded-full border border-violet-500/50">
                <Crown className="w-3 h-3 inline mr-1" />
                Premium Feature
              </span>
            ) : prompt.isUpsell ? (
              <span className="text-xs bg-emerald-500/30 text-emerald-200 px-2 py-1 rounded-full border border-emerald-500/50">
                <Check className="w-3 h-3 inline mr-1" />
                Saved Successfully
              </span>
            ) : (
              <span className="text-xs bg-cyan-500/30 text-cyan-200 px-2 py-1 rounded-full border border-cyan-500/30">
                <Star className="w-3 h-3 inline mr-1" />
                Plus Feature
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-slate-300 text-sm mb-4">{prompt.description}</p>
          
          <ul className="space-y-2 mb-6">
            {prompt.features.map((feat, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Check className={`w-4 h-4 mt-0.5 shrink-0 ${
                  isPremiumOnly ? 'text-violet-400' : prompt.isUpsell ? 'text-emerald-400' : 'text-cyan-400'
                }`} />
                <span className="text-slate-300">{feat}</span>
              </li>
            ))}
          </ul>

          {/* CTA Buttons */}
          <div className="space-y-2">
            <Link to="/pricing" onClick={onClose}>
              <Button 
                className={`w-full py-5 ${
                  isPremiumOnly 
                    ? 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500' 
                    : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500'
                } text-white font-semibold`}
              >
                {isPremiumOnly ? (
                  <Crown className="w-5 h-5 mr-2" />
                ) : (
                  <Star className="w-5 h-5 mr-2" />
                )}
                {prompt.cta}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            
            <Button 
              variant="ghost" 
              onClick={onClose}
              className="w-full text-slate-400 hover:text-slate-300"
            >
              {prompt.isUpsell ? "Continue Writing" : "Maybe Later"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Hook to check if a feature is available for current tier
export const useFeatureAccess = (userTier = 'free') => {
  const tier = userTier?.toLowerCase() || 'free';
  
  const tierLevels = {
    'free': 0,
    'promo': 2,
    'storyteller': 1,
    'plus': 1,
    'pro': 1,
    'author': 2,
    'premium': 2,
    'family': 2,
  };
  
  const userLevel = tierLevels[tier] ?? 0;
  
  return {
    canExport: userLevel >= 1,
    canImport: userLevel >= 1,
    canVoice: userLevel >= 1,
    canFullBook: userLevel >= 2,
    canContinueStyle: userLevel >= 2,
    maxProjects: tier === 'free' ? 1 : tier === 'author' || tier === 'premium' || tier === 'family' ? 100 : 10,
    maxChapters: tier === 'free' ? 15 : tier === 'author' || tier === 'premium' || tier === 'family' ? -1 : 30,
    tierName: tier,
    isPaid: userLevel > 0,
    isPremium: userLevel >= 2,
  };
};

export default UpgradePrompt;
